import { useEffect, useState } from "react";
import type {
  Actuacion,
  Documento,
  EstadoChecklistDocumental,
  EtapaChecklistItem,
  EtapaDocumentoSugerido,
  EtapaProceso,
  EventoAgenda,
  Expediente,
  ExpedienteEtapaHistorialItem,
  MovimientoEconomico,
  Usuario,
} from "../../../shared/types";
import { ESTADOS_CHECKLIST_DOCUMENTAL } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { Cargando } from "../../components/Cargando";
import { Select } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { PanelCronometro } from "./PanelCronometro";

function diasEntre(desde: string, hasta: Date = new Date()): number {
  const ms = hasta.getTime() - new Date(desde).getTime();
  return Math.max(Math.round(ms / 86400000), 0);
}

function fechaMasReciente(fechas: string[]): string | null {
  if (fechas.length === 0) return null;
  return fechas.reduce((max, f) => (f > max ? f : max));
}

interface ChecklistEstado extends EtapaChecklistItem {
  completado: boolean;
  completado_en: string | null;
  completado_por: string | null;
  estado: EstadoChecklistDocumental | null;
  responsable_id: string | null;
  responsable_nombre?: string;
  fecha_solicitud: string | null;
  fecha_recepcion: string | null;
  observacion: string | null;
  documento_id: string | null;
  documento_titulo?: string;
}

function Dato({ etiqueta, valor, alerta }: { etiqueta: string; valor: string; alerta?: boolean }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">{etiqueta}</p>
      <p className={`mt-1 text-sm ${alerta ? "font-medium text-error" : "text-texto"}`}>{valor}</p>
    </div>
  );
}

// Ficha operativa del caso: resumen siempre visible arriba del timeline de
// etapas (Requerimiento "Ficha Operativa" — reutiliza esta misma pestaña,
// no crea una ficha paralela). Deriva todo de datos ya cargados por
// ExpedienteFichaPage (actuaciones/eventosAgenda/documentos) — cero
// fetches nuevos.
function ResumenOperativo({
  expediente,
  etapaActual,
  vencida,
  checklist,
  actuaciones,
  eventosAgenda,
}: {
  expediente: Expediente;
  etapaActual: EtapaProceso | undefined;
  vencida: boolean;
  checklist: ChecklistEstado[];
  actuaciones: Actuacion[];
  eventosAgenda: EventoAgenda[];
}) {
  const requeridos = checklist.filter((c) => c.obligatorio && c.estado !== "No requerido");
  const completos = requeridos.filter((c) => c.estado === "Recibido" || c.estado === "Validado" || (c.estado == null && c.completado));
  const pctDocumentacion = requeridos.length > 0 ? Math.round((completos.length / requeridos.length) * 100) : null;
  const faltantes = requeridos.length - completos.length;

  const proximaAccion = checklist.find(
    (c) => c.obligatorio && !(c.estado === "Recibido" || c.estado === "Validado" || (c.estado == null && c.completado)),
  );

  const eventosPendientes = eventosAgenda.filter((e) => e.estado === "Pendiente").sort((a, b) => (a.fecha_vencimiento < b.fecha_vencimiento ? -1 : 1));
  const proximoVencimiento = eventosPendientes[0];
  const hoy = new Date().toISOString().substring(0, 10);
  const vencimientosVencidos = eventosPendientes.filter((e) => e.fecha_vencimiento < hoy);

  const ultimoMovimiento = fechaMasReciente(actuaciones.map((a) => a.fecha));
  const diasSinMovimiento = diasEntre(ultimoMovimiento ?? expediente.creado_en);

  const observados = checklist.filter((c) => c.estado === "Observado");

  const alertas: string[] = [];
  if (vencida) alertas.push("Etapa vencida");
  if (vencimientosVencidos.length > 0) alertas.push(`${vencimientosVencidos.length} vencimiento(s) atrasado(s)`);
  if (observados.length > 0) alertas.push(`${observados.length} ítem(s) de checklist observado(s)`);
  if (diasSinMovimiento >= 30) alertas.push(`Sin movimiento hace ${diasSinMovimiento} días`);

  return (
    <Tarjeta className="p-5">
      <h3 className="mb-4 text-sm font-semibold text-texto">Ficha operativa</h3>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        <Dato etiqueta="Etapa actual" valor={etapaActual?.nombre ?? "Sin etapa"} />
        <Dato etiqueta="Responsable actual" valor={expediente.etapa_responsable_nombre ?? "Sin responsable"} />
        <Dato etiqueta="Próxima acción" valor={proximaAccion?.texto ?? "Sin acciones pendientes registradas"} />
        <Dato
          etiqueta="Próximo vencimiento"
          valor={proximoVencimiento ? `${proximoVencimiento.fecha_vencimiento} — ${proximoVencimiento.titulo}` : "Sin vencimientos pendientes"}
          alerta={proximoVencimiento ? proximoVencimiento.fecha_vencimiento < hoy : false}
        />
        <Dato
          etiqueta="Documentación completa"
          valor={pctDocumentacion != null ? `${pctDocumentacion}% (${completos.length}/${requeridos.length})` : "Sin checklist"}
        />
        <Dato etiqueta="Documentación faltante" valor={requeridos.length > 0 ? `${faltantes} ítem(s)` : "—"} alerta={faltantes > 0} />
        <Dato etiqueta="Último movimiento" valor={ultimoMovimiento ?? "Sin actuaciones registradas"} />
        <Dato etiqueta="Días sin movimiento" valor={`${diasSinMovimiento} días`} alerta={diasSinMovimiento >= 30} />
      </div>
      <div className="mt-4 border-t border-borde pt-4">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-texto-secundario">Riesgos o alertas activas</p>
        {alertas.length === 0 ? (
          <p className="text-sm text-texto-secundario">Sin riesgos detectados.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {alertas.map((a) => (
              <Insignia key={a} texto={a} claveColor="Vencido" />
            ))}
          </div>
        )}
      </div>
    </Tarjeta>
  );
}

function FilaChecklist({
  item,
  puedeMover,
  usuarios,
  documentos,
  onActualizar,
}: {
  item: ChecklistEstado;
  puedeMover: boolean;
  usuarios: Usuario[];
  documentos: Documento[];
  onActualizar: (cambios: Partial<{
    estado: EstadoChecklistDocumental;
    responsable_id: string | null;
    fecha_solicitud: string | null;
    fecha_recepcion: string | null;
    observacion: string | null;
    documento_id: string | null;
  }>) => void;
}) {
  const [expandido, setExpandido] = useState(false);
  const completoBinario = item.estado == null && item.completado;

  return (
    <li className="py-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          className="flex-1 text-left text-texto hover:text-secundario"
        >
          {item.categoria && (
            <span className="mr-1.5 rounded bg-fondo px-1.5 py-0.5 text-[10px] font-medium uppercase text-texto-secundario">
              {item.categoria}
            </span>
          )}
          <span className={completoBinario ? "text-texto-secundario line-through" : ""}>{item.texto}</span>
        </button>
        {item.obligatorio && !completoBinario && item.estado !== "Recibido" && item.estado !== "Validado" && (
          <Insignia texto="Obligatorio" claveColor="Pendiente" />
        )}
        {puedeMover ? (
          <Select
            className="w-auto text-xs"
            value={item.estado ?? ""}
            onChange={(e) => onActualizar({ estado: (e.target.value || "Pendiente") as EstadoChecklistDocumental })}
          >
            <option value="" disabled>
              {item.completado ? "Completado (binario)" : "Sin estado"}
            </option>
            {ESTADOS_CHECKLIST_DOCUMENTAL.map((estado) => (
              <option key={estado} value={estado}>
                {estado}
              </option>
            ))}
          </Select>
        ) : (
          item.estado && <Insignia texto={item.estado} />
        )}
      </div>
      {expandido && (
        <div className="mt-2 grid grid-cols-1 gap-2 rounded-lg border border-borde bg-fondo p-3 sm:grid-cols-2">
          <label className="block text-xs text-texto-secundario">
            Responsable
            <Select
              className="mt-1"
              value={item.responsable_id ?? ""}
              disabled={!puedeMover}
              onChange={(e) => onActualizar({ responsable_id: e.target.value || null })}
            >
              <option value="">Sin asignar</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre_completo}
                </option>
              ))}
            </Select>
          </label>
          <label className="block text-xs text-texto-secundario">
            Documento vinculado
            <Select
              className="mt-1"
              value={item.documento_id ?? ""}
              disabled={!puedeMover}
              onChange={(e) => onActualizar({ documento_id: e.target.value || null })}
            >
              <option value="">Sin vincular</option>
              {documentos.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.titulo}
                </option>
              ))}
            </Select>
          </label>
          <label className="block text-xs text-texto-secundario">
            Fecha de solicitud
            <input
              type="date"
              className="mt-1 w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm text-texto"
              value={item.fecha_solicitud ?? ""}
              disabled={!puedeMover}
              onChange={(e) => onActualizar({ fecha_solicitud: e.target.value || null })}
            />
          </label>
          <label className="block text-xs text-texto-secundario">
            Fecha de recepción
            <input
              type="date"
              className="mt-1 w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm text-texto"
              value={item.fecha_recepcion ?? ""}
              disabled={!puedeMover}
              onChange={(e) => onActualizar({ fecha_recepcion: e.target.value || null })}
            />
          </label>
          <label className="block text-xs text-texto-secundario sm:col-span-2">
            Observación
            <textarea
              className="mt-1 w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm text-texto"
              rows={2}
              value={item.observacion ?? ""}
              disabled={!puedeMover}
              onChange={(e) => onActualizar({ observacion: e.target.value || null })}
              onBlur={(e) => onActualizar({ observacion: e.target.value || null })}
            />
          </label>
          {item.documento_titulo && <p className="text-xs text-texto-secundario sm:col-span-2">Documento vinculado: {item.documento_titulo}</p>}
        </div>
      )}
    </li>
  );
}

// Motor de Procesos de Trabajo dentro de la ficha del expediente: si no
// tiene proceso asignado, la asignación se hace desde "Editar expediente"
// (mismo select que Unidad de Negocio, ver FormularioExpediente.tsx) — acá
// sólo se opera el proceso ya asignado: ficha operativa, timeline de
// etapas, checklist documental extendido, documentos sugeridos, mover de
// etapa e historial. Ver routes/procesos.ts y routes/expedientes.ts
// (PATCH /:id/etapa, PATCH /:id/etapa/checklist).
export function PestanaProceso({
  expediente,
  actuaciones,
  eventosAgenda,
  documentos,
  movimientos,
  onCambio,
}: {
  expediente: Expediente;
  actuaciones: Actuacion[];
  eventosAgenda: EventoAgenda[];
  documentos: Documento[];
  movimientos: MovimientoEconomico[];
  onCambio: () => void;
}) {
  const { permisos } = useAuth();
  const puedeMover = permisos.has("procesos.mover_etapa");
  const { mostrar } = useToast();

  const [etapas, setEtapas] = useState<EtapaProceso[]>([]);
  const [checklist, setChecklist] = useState<ChecklistEstado[]>([]);
  const [documentosSugeridos, setDocumentosSugeridos] = useState<EtapaDocumentoSugerido[]>([]);
  const [historial, setHistorial] = useState<ExpedienteEtapaHistorialItem[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [cargando, setCargando] = useState(true);
  const [etapaDestino, setEtapaDestino] = useState("");
  const [moviendo, setMoviendo] = useState(false);

  const procesoId = expediente.proceso_id;

  async function cargar() {
    if (!procesoId) {
      setCargando(false);
      return;
    }
    setCargando(true);
    try {
      const [etapasResp, checklistResp, historialResp] = await Promise.all([
        api.get<{ items: EtapaProceso[] }>(`/procesos/${procesoId}/etapas`),
        api.get<{ items: ChecklistEstado[] }>(`/expedientes/${expediente.id}/etapa/checklist`),
        api.get<{ items: ExpedienteEtapaHistorialItem[] }>(`/expedientes/${expediente.id}/etapa/historial`),
      ]);
      setEtapas(etapasResp.items);
      setChecklist(checklistResp.items);
      setHistorial(historialResp.items);
      if (expediente.etapa_actual_id) {
        const docs = await api.get<{ items: EtapaDocumentoSugerido[] }>(
          `/procesos/etapas/${expediente.etapa_actual_id}/documentos-sugeridos`,
        );
        setDocumentosSugeridos(docs.items);
      } else {
        setDocumentosSugeridos([]);
      }
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cargar el Proceso.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expediente.id, procesoId, expediente.etapa_actual_id]);

  useEffect(() => {
    // Algunos roles no tienen usuarios.ver por defecto — degrada sin
    // romper (el selector de responsable del checklist queda vacío).
    api
      .get<{ items: Usuario[] }>("/usuarios")
      .then((d) => setUsuarios(d.items.filter((u) => u.activo)))
      .catch(() => undefined);
  }, []);

  async function actualizarChecklist(
    item: ChecklistEstado,
    cambios: Partial<{
      estado: EstadoChecklistDocumental;
      responsable_id: string | null;
      fecha_solicitud: string | null;
      fecha_recepcion: string | null;
      observacion: string | null;
      documento_id: string | null;
    }>,
  ) {
    try {
      await api.patch(`/expedientes/${expediente.id}/etapa/checklist`, {
        checklist_item_id: item.id,
        ...cambios,
      });
      cargar();
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo actualizar el checklist.", "error");
    }
  }

  async function mover() {
    if (!etapaDestino) return;
    setMoviendo(true);
    try {
      await api.patch(`/expedientes/${expediente.id}/etapa`, { etapa_id: etapaDestino });
      mostrar("Expediente movido de etapa.", "exito");
      setEtapaDestino("");
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo mover el expediente de etapa.", "error");
    } finally {
      setMoviendo(false);
    }
  }

  if (!procesoId) {
    return (
      <Tarjeta className="p-6 text-center">
        <p className="text-sm font-semibold text-texto">Sin Proceso de Trabajo asignado</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-texto-secundario">
          Este expediente todavía no está asignado a ningún Proceso de Trabajo. Para asignarle uno, editá el
          expediente desde la pestaña "Datos" y elegí un Proceso.
        </p>
      </Tarjeta>
    );
  }

  if (cargando) return <Cargando />;

  const etapaActual = etapas.find((e) => e.id === expediente.etapa_actual_id);
  const diasEnEtapa = expediente.etapa_desde ? diasEntre(expediente.etapa_desde) : null;
  const vencida =
    diasEnEtapa != null && etapaActual?.tiempo_objetivo_dias != null && diasEnEtapa > etapaActual.tiempo_objetivo_dias;
  const etapasActivas = etapas.filter((e) => e.activo);

  // Agrupa por categoría (checklist documental extendido) preservando el
  // orden ya provisto por el backend; ítems sin categoría van en un grupo
  // "General" al final.
  const categorias = Array.from(new Set(checklist.map((c) => c.categoria).filter((c): c is string => Boolean(c))));

  return (
    <div className="space-y-6">
      <ResumenOperativo
        expediente={expediente}
        etapaActual={etapaActual}
        vencida={Boolean(vencida)}
        checklist={checklist}
        actuaciones={actuaciones}
        eventosAgenda={eventosAgenda}
      />

      <PanelCronometro
        expediente={expediente}
        actuaciones={actuaciones}
        movimientos={movimientos}
        onCambio={onCambio}
      />

      <Tarjeta className="p-5">
        <h3 className="mb-4 text-sm font-semibold text-texto">{expediente.proceso_nombre ?? "Proceso"}</h3>
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {etapasActivas.map((et, i) => {
            const esActual = et.id === expediente.etapa_actual_id;
            return (
              <div key={et.id} className="flex shrink-0 items-center gap-1">
                <div
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
                    esActual ? "border-secundario bg-secundario/10 text-secundario" : "border-borde text-texto-secundario"
                  }`}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full border border-borde"
                    style={{ backgroundColor: et.color ?? undefined }}
                  />
                  {et.nombre}
                </div>
                {i < etapasActivas.length - 1 && <span className="text-texto-secundario">→</span>}
              </div>
            );
          })}
        </div>

        {etapaActual ? (
          <div className="mt-4 grid grid-cols-2 gap-4 border-t border-borde pt-4 sm:grid-cols-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">Etapa actual</p>
              <p className="mt-1 text-sm text-texto">{etapaActual.nombre}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">Responsable</p>
              <p className="mt-1 text-sm text-texto">{expediente.etapa_responsable_nombre ?? "Sin responsable"}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">Tiempo en etapa</p>
              <p className="mt-1 text-sm text-texto">
                {diasEnEtapa != null ? `${diasEnEtapa} días` : "—"}
                {etapaActual.tiempo_objetivo_dias != null ? ` / ${etapaActual.tiempo_objetivo_dias} días` : ""}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">Estado</p>
              <p className="mt-1">
                <Insignia texto={vencida ? "Vencida" : "En plazo"} claveColor={vencida ? "Vencido" : "Cumplido"} />
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-4 border-t border-borde pt-4 text-sm text-texto-secundario">
            Este expediente todavía no tiene una etapa asignada dentro del proceso.
          </p>
        )}

        {puedeMover && (
          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-borde pt-4">
            <div className="max-w-xs flex-1">
              <label className="mb-1 block text-xs font-medium text-texto">Mover a la etapa</label>
              <Select value={etapaDestino} onChange={(e) => setEtapaDestino(e.target.value)}>
                <option value="">Seleccionar etapa...</option>
                {etapasActivas
                  .filter((et) => et.id !== expediente.etapa_actual_id)
                  .map((et) => (
                    <option key={et.id} value={et.id}>
                      {et.nombre}
                    </option>
                  ))}
              </Select>
            </div>
            <Boton onClick={mover} cargando={moviendo} disabled={!etapaDestino}>
              Mover
            </Boton>
          </div>
        )}
      </Tarjeta>

      {etapaActual && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Tarjeta className="p-5">
            <h4 className="mb-1 text-sm font-semibold text-texto">Checklist documental de la etapa</h4>
            {checklist.length === 0 ? (
              <p className="text-sm text-texto-secundario">Esta etapa no tiene checklist configurado.</p>
            ) : (
              <div className="space-y-4">
                {categorias.map((categoria) => (
                  <div key={categoria}>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-texto-secundario">{categoria}</p>
                    <ul className="divide-y divide-borde">
                      {checklist
                        .filter((c) => c.categoria === categoria)
                        .map((item) => (
                          <FilaChecklist
                            key={item.id}
                            item={item}
                            puedeMover={puedeMover}
                            usuarios={usuarios}
                            documentos={documentos}
                            onActualizar={(cambios) => actualizarChecklist(item, cambios)}
                          />
                        ))}
                    </ul>
                  </div>
                ))}
                {checklist.some((c) => !c.categoria) && (
                  <ul className="divide-y divide-borde">
                    {checklist
                      .filter((c) => !c.categoria)
                      .map((item) => (
                        <FilaChecklist
                          key={item.id}
                          item={item}
                          puedeMover={puedeMover}
                          usuarios={usuarios}
                          documentos={documentos}
                          onActualizar={(cambios) => actualizarChecklist(item, cambios)}
                        />
                      ))}
                  </ul>
                )}
              </div>
            )}
          </Tarjeta>

          <Tarjeta className="p-5">
            <h4 className="mb-2 text-sm font-semibold text-texto">Documentos sugeridos</h4>
            {documentosSugeridos.length === 0 ? (
              <p className="text-sm text-texto-secundario">Sin documentos sugeridos para esta etapa.</p>
            ) : (
              <ul className="list-disc space-y-1 pl-5 text-sm text-texto">
                {documentosSugeridos.map((d) => (
                  <li key={d.id}>{d.nombre}</li>
                ))}
              </ul>
            )}
          </Tarjeta>
        </div>
      )}

      <Tarjeta className="p-5">
        <h4 className="mb-2 text-sm font-semibold text-texto">Historial de etapas</h4>
        {historial.length === 0 ? (
          <p className="text-sm text-texto-secundario">Todavía no hay cambios de etapa registrados.</p>
        ) : (
          <ul className="divide-y divide-borde">
            {historial.map((h) => (
              <li key={h.id} className="py-2.5 text-sm">
                <p className="text-texto">
                  {h.etapa_anterior_nombre ? `${h.etapa_anterior_nombre} → ` : "Ingresó en "}
                  <span className="font-medium">{h.etapa_nueva_nombre}</span>
                </p>
                <p className="text-xs text-texto-secundario">
                  {h.creado_en} · {h.usuario_nombre ?? "—"}
                  {h.responsable_nuevo_nombre ? ` · Responsable: ${h.responsable_nuevo_nombre}` : ""}
                  {h.observaciones ? ` · ${h.observaciones}` : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
    </div>
  );
}
